<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MiraStaffAPI from 'dashboard/api/miraStaff';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const prompt = ref('');
const messages = ref([]);
const pending = ref(false);

const send = async () => {
  const text = prompt.value.trim();
  if (!text || pending.value || text.length > 2000) return;
  prompt.value = '';
  messages.value.push({ role: 'staff', text });
  pending.value = true;
  try {
    const { data } = await MiraStaffAPI.chat(props.conversationId, text);
    messages.value.push({ role: 'mira', text: data.reply });
  } catch {
    messages.value.push({
      role: 'error',
      text: t('CONVERSATION.MIRA.UNAVAILABLE'),
    });
  } finally {
    pending.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col flex-1 min-h-0 bg-n-surface-1"
    :aria-label="$t('CONVERSATION.MIRA.TAB')"
  >
    <div class="border-b border-n-weak px-4 py-3">
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('CONVERSATION.MIRA.HEADING') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.MIRA.PRIVATE_NOTE') }}
      </p>
    </div>
    <div
      class="flex-1 min-h-0 overflow-y-auto p-4 space-y-3"
      aria-live="polite"
    >
      <p v-if="!messages.length" class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.MIRA.EMPTY') }}
      </p>
      <div
        v-for="(message, index) in messages"
        :key="index"
        class="max-w-prose rounded-lg px-3 py-2 text-sm whitespace-pre-wrap"
        :class="
          message.role === 'staff'
            ? 'ml-auto bg-n-brand text-white'
            : message.role === 'error'
              ? 'bg-n-ruby-3 text-n-ruby-11'
              : 'bg-n-surface-2 text-n-slate-12'
        "
      >
        {{ message.text }}
      </div>
      <p v-if="pending" class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.MIRA.WORKING') }}
      </p>
    </div>
    <form
      class="border-t border-n-weak p-4 flex flex-col gap-2"
      @submit.prevent="send"
    >
      <label
        for="mira-staff-prompt"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ $t('CONVERSATION.MIRA.PROMPT_LABEL') }}
      </label>
      <textarea
        id="mira-staff-prompt"
        v-model="prompt"
        rows="3"
        maxlength="2000"
        :disabled="pending"
        :placeholder="$t('CONVERSATION.MIRA.PLACEHOLDER')"
        class="w-full rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm text-n-slate-12"
      />
      <div class="flex justify-end">
        <button
          type="submit"
          :disabled="pending || !prompt.trim()"
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {{ $t('CONVERSATION.MIRA.SEND') }}
        </button>
      </div>
    </form>
  </section>
</template>
